import React, { useMemo } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import type { OverlayTriggerProps } from 'react-bootstrap/OverlayTrigger'
import { generateId } from '@ztechapps/common.utils'
import { ShowIf } from '../ShowIf'
import { CloseButton } from '../CloseButton'
import { HorizontalStack } from '../HorizontalStack'

export type TooltipPopOverProps = Omit<OverlayTriggerProps, 'children' | 'overlay'> & {
  children: React.ReactNode
  tooltipBody?: React.ReactNode
  tooltipHeader?: React.ReactNode
  overlay?: OverlayTriggerProps['overlay']
  trigger?: OverlayTriggerProps['trigger']
}
export const TooltipPopOver = ({
  children,
  tooltipBody,
  tooltipHeader,
  onToggle,
  showHeader,
  overlay,
  ...props
}: TooltipPopOverProps & { showHeader?: boolean }) => {
  const idRef = React.useRef(generateId(5))
  const [show, setShow] = React.useState(false)

  //automatically setup the close button if there is showHeader set as true
  let header = tooltipHeader
  if (showHeader) {
    header = (
      <HorizontalStack className="justify-content-between">
        <div>{tooltipHeader}</div>
        <CloseButton onClick={() => setShow(false)} />
      </HorizontalStack>
    )
  }

  const popover = overlay ?? (
    <Popover id={`popover${idRef.current}`} className="shadow-lg">
      <ShowIf condition={!!header}>
        <Popover.Header as="h3">{header}</Popover.Header>
      </ShowIf>
      <Popover.Body>{tooltipBody}</Popover.Body>
    </Popover>
  )

  const trigger = useMemo<OverlayTriggerProps['trigger']>(() => {
    if (showHeader) return ['click']
    return ['hover', 'focus']
  }, [showHeader])

  const otherProps = useMemo<any>(() => {
    if (show && showHeader)
      return {
        rootClose: true,
        onHide: () => {
          setShow(false)
        },
      }
    return {
      rootClose: false,
      onHide: null,
    }
  }, [show])

  return (
    <OverlayTrigger
      placement="auto"
      delay={{ hide: 0, show: 500 }}
      trigger={trigger}
      show={show}
      onToggle={(e) => {
        setShow(e)
        onToggle?.(e)
      }}
      {...{ ...props, ...otherProps }}
      overlay={popover}
    >
      <span>{children}</span>
    </OverlayTrigger>
  )
}
